import type { Rect } from "../../common/geometry.js";

/** How a strip of parked stubs is measured out. See `tileMinimizedStrip`. */
export interface MinimizeStripOptions {
  /** Width of every stub. Narrowed if the work area cannot hold even one. */
  width: number;
  /** Between stubs, and between the strip and the edges of the work area. */
  gap: number;
}

/**
 * Lay a row of minimized-window stubs along the bottom of `area`, left to
 * right, starting another row above when one runs out of room.
 *
 * Takes heights rather than windows so the arithmetic can be read — and
 * tested — without a desktop: one height per parked window, in the order
 * they were minimized, and a rectangle comes back for each. A height of zero
 * means "no stub for this one" and gets a null instead of a slot, which is
 * how a window hidden while parked keeps its place in the order without
 * taking up space in the strip.
 *
 * Rows grow upward from the bottom edge, so the first thing minimized stays
 * in the corner however many join it, and a stub never moves unless
 * something ahead of it in the order does.
 */
export function tileMinimizedStrip(
  heights: readonly number[],
  area: Rect,
  options: MinimizeStripOptions,
): (Rect | null)[] {
  const slots: (Rect | null)[] = heights.map(() => null);
  const width = Math.min(options.width, Math.max(0, area.width - options.gap * 2));
  if (width <= 0) return slots;

  const gap = options.gap;
  const left = area.x + gap;
  const right = area.x + area.width - gap;
  let x = left;
  let bottom = area.y + area.height - gap;
  let rowHeight = 0;

  for (let i = 0; i < heights.length; i++) {
    const height = heights[i]!;
    if (height <= 0) continue;
    // Never wrap the first stub of a row: one too wide for the work area
    // overhangs rather than starting an empty row above itself.
    if (x > left && x + width > right) {
      x = left;
      bottom -= rowHeight + gap;
      rowHeight = 0;
    }
    rowHeight = Math.max(rowHeight, height);
    slots[i] = { x, y: bottom - height, width, height };
    x += width + gap;
  }
  return slots;
}
